import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { TravelExpenseReport } from "../types/expense";

interface TimeRangeFormProps {
  reportInfo: Partial<TravelExpenseReport>;
  onReportInfoChange: (reportInfo: Partial<TravelExpenseReport>) => void;
}

const TimeRangeForm: React.FC<TimeRangeFormProps> = ({
  reportInfo,
  onReportInfoChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const options = [
    { value: "", label: "Zeitraum auswählen" },
    { value: "Januar - März", label: "Januar - März" },
    { value: "April - Juni", label: "April - Juni" },
    { value: "Juli - September", label: "Juli - September" },
    { value: "Oktober - Dezember", label: "Oktober - Dezember" },
  ];

  const selectedOption = options.find(
    (option) => option.value === (reportInfo.quarterPeriod || ""),
  );

  const handleSelect = (value: string) => {
    onReportInfoChange({
      ...reportInfo,
      quarterPeriod: value,
    });
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideTrigger = triggerRef.current?.contains(target);
      const dropdownElement = document.querySelector(".custom-select-dropdown");
      const isClickInsideDropdown = dropdownElement?.contains(target);

      if (!isClickInsideTrigger && !isClickInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  // Reposition dropdown on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 2,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  return (
    <>
      <div className="custom-select-container" ref={dropdownRef}>
        <div
          ref={triggerRef}
          className={`custom-select-trigger ${isOpen ? "open" : ""}`}
          onClick={toggleDropdown}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleDropdown();
            }
          }}
        >
          <span className="custom-select-label">
            {selectedOption?.label || "Zeitraum auswählen"}
          </span>
          <i
            className={`fas fa-chevron-down custom-select-arrow ${isOpen ? "rotated" : ""}`}
          ></i>
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="custom-select-dropdown"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            <div className="custom-select-options">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`custom-select-option ${
                    option.value === (reportInfo.quarterPeriod || "")
                      ? "selected"
                      : ""
                  } ${option.value === "" ? "placeholder" : ""}`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={
                    option.value === (reportInfo.quarterPeriod || "")
                  }
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TimeRangeForm;
